/**
 * QuorumProgressBar Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 6: Create QuorumProgressBar component
 *
 * Visual progress bar showing vote count vs quorum threshold.
 * Shows absolute counts (not percentages) per TD-8 spec.
 *
 * @see AC-4.2.7.4 - Shows X/Y votes with quorum met indicator
 *
 * @module components/governance/QuorumProgressBar
 */

import { Check } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface QuorumProgressBarProps {
  /** Current vote count */
  currentVotes: number;
  /** Required votes for quorum */
  requiredVotes: number;
  /** Show detailed breakdown */
  showLabel?: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Quorum progress bar with absolute counts
 */
export function QuorumProgressBar({
  currentVotes,
  requiredVotes,
  showLabel = true,
}: QuorumProgressBarProps) {
  const percentage = Math.min((currentVotes / requiredVotes) * 100, 100);
  const quorumMet = currentVotes >= requiredVotes;
  const votesNeeded = requiredVotes - currentVotes;

  return (
    <div className="space-y-2" data-testid="quorum-progress-bar">
      {/* Progress bar */}
      <div className="relative h-3 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            quorumMet ? 'bg-green-500' : 'bg-primary'
          }`}
          style={{ width: `${percentage}%` }}
          data-testid="quorum-progress-fill"
          role="progressbar"
          aria-valuenow={currentVotes}
          aria-valuemin={0}
          aria-valuemax={requiredVotes}
          aria-label={`${currentVotes} of ${requiredVotes} votes`}
        />
      </div>

      {/* Labels */}
      {showLabel && (
        <div className="flex items-center justify-between text-sm">
          <span
            className="text-muted-foreground"
            data-testid="quorum-progress-count"
          >
            {currentVotes}/{requiredVotes} votes
          </span>

          {quorumMet ? (
            <span
              className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium"
              data-testid="quorum-met-indicator"
            >
              <Check className="w-4 h-4" />
              Quorum met
            </span>
          ) : (
            <span
              className="text-muted-foreground"
              data-testid="quorum-needed-text"
            >
              Need {votesNeeded} more vote{votesNeeded !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default QuorumProgressBar;
